import { useState, useRef, useLayoutEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Info } from 'lucide-react'

interface TooltipProps {
  content: string | React.ReactNode
  children?: React.ReactNode
  /** Maximum width in px (default 384 = max-w-sm) */
  maxWidth?: number
}

export function Tooltip({ content, children, maxWidth = 384 }: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false)
  const [layout, setLayout] = useState<{
    top: number
    left: number
    arrowLeft: number
    place: 'top' | 'bottom'
  } | null>(null)
  const triggerRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  const compute = useCallback(() => {
    const trigger = triggerRef.current
    const tip = tooltipRef.current
    if (!trigger || !tip) return

    const pad = 12
    const gap = 8
    const a = trigger.getBoundingClientRect()
    const t = tip.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight

    // Horizontal: centre on trigger, clamp within viewport
    const centerX = a.left + a.width / 2
    const idealLeft = centerX - t.width / 2
    const left = Math.min(Math.max(idealLeft, pad), vw - t.width - pad)

    // Arrow tracks the trigger centre relative to the tooltip box
    const arrowLeft = Math.min(Math.max(centerX - left, 12), t.width - 12)

    // Vertical: prefer top, fall back to bottom
    const topCandidate = a.top - t.height - gap
    const bottomCandidate = a.bottom + gap
    const place: 'top' | 'bottom' =
      topCandidate >= pad
        ? 'top'
        : bottomCandidate + t.height <= vh - pad
          ? 'bottom'
          : 'top'
    const top =
      place === 'top'
        ? Math.max(topCandidate, pad)
        : Math.min(bottomCandidate, vh - t.height - pad)

    setLayout({ top, left, arrowLeft, place })
  }, [])

  useLayoutEffect(() => {
    if (!isVisible) {
      setLayout(null)
      return
    }

    const raf = requestAnimationFrame(compute)
    window.addEventListener('resize', compute)
    window.addEventListener('scroll', compute, true)

    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', compute)
      window.removeEventListener('scroll', compute, true)
    }
  }, [isVisible, compute])

  return (
    <>
      <div className="relative inline-block">
        <div
          ref={triggerRef}
          onMouseEnter={() => setIsVisible(true)}
          onMouseLeave={() => setIsVisible(false)}
          className="cursor-help"
        >
          {children || (
            <Info className="h-4 w-4 text-[var(--text-muted)] hover:text-[var(--text-secondary)]" />
          )}
        </div>
      </div>

      {isVisible &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={tooltipRef}
            className="fixed z-[9999] px-3 py-2 text-xs text-white bg-gray-900 rounded-lg shadow-lg whitespace-normal pointer-events-none transition-opacity duration-100"
            style={{
              maxWidth: `min(${maxWidth}px, calc(100vw - 24px))`,
              top: layout ? layout.top : -9999,
              left: layout ? layout.left : -9999,
              opacity: layout ? 1 : 0,
            }}
          >
            {content}
            {/* Arrow */}
            {layout && (
              <span
                className={
                  layout.place === 'top'
                    ? 'absolute top-full border-4 border-transparent border-t-gray-900'
                    : 'absolute bottom-full border-4 border-transparent border-b-gray-900'
                }
                style={{ left: layout.arrowLeft, transform: 'translateX(-50%)' }}
              />
            )}
          </div>,
          document.body,
        )}
    </>
  )
}
